import React, { useEffect } from 'react'
import cx from 'classnames'
import styled from 'styled-components'
import DatePickerField from './DatePickerField'
import CurrencyField from './CurrencyField'
import NumberField from './NumberField'
import actionCodes from '../../context/actionCodes'
import { useMessages } from '../../context/messages'
import { UIService } from '../../services'
import {
  StyledFieldLabel,
  StyledFieldInput,
  StyledFieldDesc,
  StyledFieldError,
  StyledFieldContainer,
  StyledTextTitle,
  StyledTextDesc,
  StyledAutosizeTextarea,
} from '../styles'
import { clearValue, formatNumber, isNumber } from '../../helpers/form'
import { getToken, setToken, deletePropertyPath } from '../../helpers'
import { LOCAL_STORAGE_KEYS } from '../../constants'
import { FormikProps } from 'formik'
import merge from 'ts-deepmerge'
import { ListField } from './ListField'

const StyledCheckboxContainer = styled.div`
  display: flex;
  margin: 16px 0 0;
  align-items: center;

  ${StyledFieldInput} {
    margin: 0;
  }

  ${StyledFieldLabel} {
    line-height: 17px;
  }

  ${StyledFieldLabel} {
    margin: 0 0 0 10px;
  }
`

// label = 0
// text = 1
// checkbox = 2
// list = 3
// datetime = 4
// number = 5
// currency = 6

const updateCode = (
  caseId: string,
  code: string,
  name: string,
  val: string | boolean | string[],
  touched: boolean = false
) => {
  actionCodes.addCode(caseId, code, name, val, touched)
}

interface FieldProps {
  id?: string
  hidden?: boolean
  calculated?: boolean
  type?: string
  value?: string | number | readonly string[]
  readOnly?: boolean
  round?: boolean
  label?: string
  description?: string
  options?: any
  maxValues?: number
  formik?: FormikProps<{}>
  name?: string
  code?: string
  shouldCache?: boolean
  caseId?: string
  flowId?: string
  flowInstanceId?: string
  minDate?: string | Date
  maxDate?: string | Date
  excludeHolidays?: boolean
  excludeWeekends?: boolean
  excludedDates?: number[]
  currencySymbol?: string
  style?: string
}

const Field: React.FC<FieldProps> = React.memo((props) => {
  const {
    type,
    label,
    description,
    options,
    formik,
    name,
    code,
    shouldCache,
    caseId,
    flowId,
    flowInstanceId,
    style,
    maxValues,
    ...rest
  } = props
  const error = formik.touched[name] && formik.errors[name]
  const formattedName = name

  const updateCodes = (name: string, value: string | boolean | string[]) => {
    if (formik.errors[name]) {
      actionCodes.addError(name)
    } else {
      actionCodes.removeError(name)

      for (const key of Object.keys(formik.values)) {
        if (key === name) {
          updateCode(caseId, code, key, value, true)
        } else {
          updateCode(caseId, code, key, formik.values[key])
        }
      }
    }
  }

  const { addToast } = useMessages()

  const updateLocalCache = (
    key: string,
    caseId: string,
    flowId: string,
    code: string,
    flowInstanceId: string,
    name: string,
    value: any
  ) => {
    let cached = getToken(key)

    if (!cached) {
      cached = {}
    }

    cached = merge.withOptions({ mergeArrays: false }, cached, {
      [caseId]: {
        [flowId]: { [flowInstanceId]: { [code]: { [name]: value } } },
      },
    })

    setToken(key, cached)
  }

  const clearCacheField = (key, caseId, flowId, flowInstanceId, code, name) => {
    let cached = getToken(key)
    if (!cached?.[caseId]?.[flowId]?.[flowInstanceId]?.[code]?.[name]) {
      return false
    }
    let path = `${caseId}.${flowId}.${flowInstanceId}.${code}.${name}`
    deletePropertyPath(cached, path)

    setToken(key, cached)
    return true
  }

  // On blur we should update actionCodes as some fields might be reference
  // to others.
  const handleCustomBlur = (e, isList?: boolean) => {
    formik.handleBlur(e)
    const {
      name,
      value,
      checked,
      type,
    }: {
      name: string
      value: string
      checked?: boolean
      type: string
    } = e.target

    const newVal = formatValue(value, type, props.type, props.round, checked)
    let newSelectValue = null

    // Handling in case of list type
    if (isList) {
      newSelectValue = formik.values[name]
    }

    const finalValue = !!newSelectValue ? newSelectValue : newVal

    if (value.length === 0 && type !== 'checkbox') {
      const cleared = clearCacheField(
        LOCAL_STORAGE_KEYS.CACHE,
        caseId,
        flowId,
        flowInstanceId,
        code,
        name
      )

      if (cleared) {
        const user = getToken(LOCAL_STORAGE_KEYS.USER)
        if (user.cacheConfiguredFlowIds.includes(flowId)) {
          UIService.deleteCustomerCache(
            caseId,
            flowId,
            flowInstanceId,
            code,
            name
          )
            .then(() => {})
            .catch(() => {
              addToast('error', 'Error caching input')
            })
        }
        return
      }
    }

    if (
      shouldCache &&
      (finalValue || isNumber(finalValue)) &&
      type !== 'checkbox'
    ) {
      // isNumber handles the case for 0 input
      // set token on local storage
      let payload = {}
      payload[name] = finalValue
      updateLocalCache(
        LOCAL_STORAGE_KEYS.CACHE,
        caseId,
        flowId,
        code,
        flowInstanceId,
        name,
        payload[name]
      )

      const user = getToken(LOCAL_STORAGE_KEYS.USER)
      if (user.cacheConfiguredFlowIds.includes(flowId)) {
        UIService.postCustomerCache(
          caseId,
          flowId,
          flowInstanceId,
          code,
          payload
        ).then(() => {})
      }
    }
    updateCodes(name, finalValue)

    formik.validateForm(formik.values).then(() => formik.setFieldTouched(name))
    return null
  }

  const handleCustomChange = (e, isList?: boolean) => {
    const {
      name,
      value,
      checked,
      type,
    }: { name: string; value: string; checked?: boolean; type: string } =
      e.target

    const newVal = formatValue(value, type, props.type, props.round, checked)
    let newSelectValue = null

    // Handling in case of list type
    if (isList) {
      const selectedValues = formik.values[name]
      if (selectedValues.includes(newVal) && !!newVal) {
        newSelectValue = selectedValues?.filter((val: string) => val !== newVal)
        formik.setFieldValue(formattedName, newSelectValue)
      } else if (selectedValues.length < maxValues) {
        newSelectValue = [...selectedValues, newVal]
        formik.setFieldValue(formattedName, newSelectValue)
      } else if (selectedValues.length === maxValues) {
        newSelectValue = [...selectedValues]
        newSelectValue.shift()
        if (!!newVal) newSelectValue.push(newVal)
        formik.setFieldValue(formattedName, newSelectValue)
      } else {
        newSelectValue = [...selectedValues]
      }
    } else {
      formik.handleChange(e)
    }

    const finalValue = !!newSelectValue ? newSelectValue : newVal

    if (shouldCache && type === 'checkbox') {
      // set token on local storage
      let payload = {}
      payload[name] = finalValue
      updateLocalCache(
        LOCAL_STORAGE_KEYS.CACHE,
        caseId,
        flowId,
        code,
        flowInstanceId,
        name,
        finalValue
      )

      const user = getToken(LOCAL_STORAGE_KEYS.USER)
      if (user?.cacheConfiguredFlowIds?.includes(flowId)) {
        UIService.postCustomerCache(
          caseId,
          flowId,
          flowInstanceId,
          code,
          payload
        )
      }
    }
    updateCodes(name, finalValue)
    formik.validateForm(formik.values).then(() => formik.setFieldTouched(name))
    return null
  }

  const formatValue = (
    value: string,
    fieldType: string,
    type: string,
    round?: boolean,
    checked?: boolean
  ) => {
    let newVal = fieldType === 'checkbox' && type !== '3' ? checked : value

    if (type === '5' || type === '6') {
      newVal = value !== '' ? formatNumber(round, clearValue(value)) : ''
    }

    if (type === '4' && value) {
      const newDate = new Date(value)
      newVal = newDate.toISOString()
    }
    return newVal
  }

  const extraClassName = cx({
    hidden: props.hidden,
    calculated: props.calculated,
    error: formik.errors[name],
  })

  const calcDesc = actionCodes.replaceValue(caseId, description, '')

  useEffect(() => {
    let isMounted = true
    // For calculated fields, update the formik with latest value
    if (formik?.values[name] !== rest?.value && props.readOnly && isMounted) {
      formik.setFieldValue(name, rest?.value)
    }

    return () => {
      isMounted = false
    }
  }, [props.readOnly, name, rest.value, formik])

  switch (type) {
    //label
    case '0':
      return (
        <StyledFieldContainer className={extraClassName}>
          {label && (
            <StyledTextTitle
              className="field-title"
              dangerouslySetInnerHTML={{ __html: label }}
            />
          )}
          {calcDesc && (
            <StyledTextDesc
              className="field-desc"
              dangerouslySetInnerHTML={{ __html: calcDesc }}
            />
          )}
        </StyledFieldContainer>
      )
    //text
    case '1':
      return (
        <StyledFieldContainer className={extraClassName}>
          <StyledFieldLabel htmlFor={formattedName}>{label}</StyledFieldLabel>
          <StyledAutosizeTextarea
            {...rest}
            name={formattedName}
            maxRows={4}
            cacheMeasurements
            error={error}
            onBlur={handleCustomBlur}
            onChange={handleCustomChange}
            disabled={props.readOnly}
          />
          {error && <StyledFieldError resizable>{error}</StyledFieldError>}
          {calcDesc && (
            <StyledFieldDesc dangerouslySetInnerHTML={{ __html: calcDesc }} />
          )}
        </StyledFieldContainer>
      )
    //checkbox
    case '2':
      return (
        <StyledFieldContainer className={extraClassName}>
          <StyledCheckboxContainer>
            <StyledFieldInput
              {...rest}
              name={formattedName}
              type="checkbox"
              error={error}
              onBlur={handleCustomBlur}
              onChange={handleCustomChange}
              disabled={props.readOnly}
              value={props.value}
              defaultChecked={!!props.value}
            />
            <StyledFieldLabel htmlFor={name}>{label}</StyledFieldLabel>
          </StyledCheckboxContainer>
          {error && <StyledFieldError>{error}</StyledFieldError>}
          {calcDesc && (
            <StyledFieldDesc dangerouslySetInnerHTML={{ __html: calcDesc }} />
          )}
        </StyledFieldContainer>
      )
    //list
    case '3':
      return (
        <ListField
          options={options}
          value={props.value}
          readOnly={props.readOnly}
          description={calcDesc}
          formattedName={formattedName}
          label={label}
          error={error}
          style={style}
          extraClassName={extraClassName}
          handleCustomBlur={handleCustomBlur}
          handleCustomChange={handleCustomChange}
          formik={formik}
          maxValues={maxValues}
          {...rest}
        />
      )

    //datetime
    case '4':
      return (
        <StyledFieldContainer className={extraClassName}>
          <StyledFieldLabel htmlFor={formattedName} className="field-title">
            {label}
          </StyledFieldLabel>
          <DatePickerField
            {...rest}
            name={formattedName}
            error={error}
            code={code}
            caseId={caseId}
            updateCode={updateCode}
            onBlur={handleCustomBlur}
            onChange={handleCustomChange}
          />
          {error && <StyledFieldError>{error}</StyledFieldError>}
          {calcDesc && (
            <StyledFieldDesc dangerouslySetInnerHTML={{ __html: calcDesc }} />
          )}
        </StyledFieldContainer>
      )
    //number
    case '5':
      return (
        <StyledFieldContainer className={extraClassName}>
          <StyledFieldLabel htmlFor={formattedName}>{label}</StyledFieldLabel>
          <NumberField
            {...rest}
            name={formattedName}
            error={error}
            updateCodes={updateCodes}
            onBlur={handleCustomBlur}
          />
          {error && <StyledFieldError>{error}</StyledFieldError>}
          {calcDesc && (
            <StyledFieldDesc dangerouslySetInnerHTML={{ __html: calcDesc }} />
          )}
        </StyledFieldContainer>
      )
    //currency
    case '6':
      const errorAmount = error ? error.split(' ').pop() : ''
      const notRoundError = error
        ? error.substring(0, error.lastIndexOf(' ')) +
          ' ' +
          parseFloat(errorAmount).toFixed(2)
        : ''

      return (
        <StyledFieldContainer className={extraClassName}>
          <StyledFieldLabel htmlFor={formattedName} className="field-title">
            {label}
          </StyledFieldLabel>
          <CurrencyField
            {...rest}
            name={formattedName}
            error={error}
            updateCodes={updateCodes}
            onBlur={handleCustomBlur}
          />
          {error && (
            <StyledFieldError>
              {props.round || isNaN(errorAmount) ? error : notRoundError}
            </StyledFieldError>
          )}
          {calcDesc && (
            <StyledFieldDesc dangerouslySetInnerHTML={{ __html: calcDesc }} />
          )}
        </StyledFieldContainer>
      )
    //default text field
    default:
      return (
        <StyledFieldContainer className={extraClassName}>
          <StyledFieldLabel htmlFor={formattedName}>{label}</StyledFieldLabel>
          <StyledFieldInput
            {...rest}
            name={formattedName}
            type="string"
            error={error}
            onBlur={handleCustomBlur}
            onChange={handleCustomChange}
            disabled={props.readOnly}
          />
          {error && <StyledFieldError>{error}</StyledFieldError>}
          {calcDesc && (
            <StyledFieldDesc dangerouslySetInnerHTML={{ __html: calcDesc }} />
          )}
        </StyledFieldContainer>
      )
  }
})

export default Field
